import {ChangeDetectionStrategy, Component, DestroyRef, effect, input, signal} from "@angular/core";
import {Base64DataUrl, EnkelGruppeModell, FargeHex} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {GruppeLogoKomponent} from "../gruppe-logo/gruppe-logo";
import {MatButton} from "@angular/material/button";
import {NgxColorsModule} from "ngx-colors";
import {TranslateModule} from "@ngx-translate/core";
import {standardGruppefarge} from "felles-ng2/modell/Gruppefarge";
import {BildeVelgerKomponent} from "web-ng2/delt/komponenter/skjema/bilde-velger/bilde-velger";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('GruppeLogoOgFargeSkjemaKomponent');

export type SkjemaType = ReturnType<typeof GruppeLogoOgFargeSkjemaKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

/**
 * Skjema for angivelse av logo og farge for gruppe.
 */
@Component({
    selector: 'gruppe-logo-og-farge-skjema',
    templateUrl: 'gruppe-logo-og-farge-skjema.html',
    styleUrl: 'gruppe-logo-og-farge-skjema.scss',
    imports: [
        FormsModule,
        GruppeLogoKomponent,
        MatButton,
        NgxColorsModule,
        TranslateModule,
        ReactiveFormsModule,
        BildeVelgerKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeLogoOgFargeSkjemaKomponent {

    /** Matcher fargeangivelse i hex-format uten transparens, eks #09afAf */
    static fargeHexRegexp = /^\s*#[0-9a-fA-F]{6}\s*$/;

    static opprettSkjema() {
        return new FormGroup({
            /** base64 enkodet logo-bilde valgt av bruker */
            logo: new FormControl<Base64DataUrl | null>(null),
            farge: new FormControl<FargeHex>(standardGruppefarge.formatHex(), {nonNullable: true, validators: [Validators.required, Validators.pattern(GruppeLogoOgFargeSkjemaKomponent.fargeHexRegexp)]}),
        });
    }

    skjema = input.required<SkjemaType>();
    /** Eventuell gruppe vi viser logo for */
    gruppe = input<EnkelGruppeModell | null>(null);

    gruppefarge = signal(standardGruppefarge.formatHex());
    /** Sann så lenge bruker er i prosessen med å velge et bilde via bilde-velger-komponenten */
    velgBildeAktiv = signal(false);
    /** Sann dersom bruker har valgt et bilde via bilde-velger-komponenten */
    erBildeValgt = signal(false);
    readonly maksAvatarStoerrelse: number;

    constructor(konfigurasjon: Appkonfigurasjon, private destroyRef: DestroyRef) {
        this.maksAvatarStoerrelse = konfigurasjon.maksAvatarStørrelse;
        effect(() => this.skjemaSatt(this.skjema()));
    }

    /** kalles når bildevelger åpnes (fil-velger-dialog vises) */
    bildevelgerAktivert() {
        this.velgBildeAktiv.set(true);
    }

    /** kalles når bruker har valgt en fil (fil-velger-dialog lukket).
     * bildefil er null dersom bruker ikke valgte noen fil. */
    bildeValgt(bildefil: File | null) {
        this.velgBildeAktiv.set(false);
        this.erBildeValgt.set(erIkkeNil(bildefil));
    }

    tilbakestill() {
        this.erBildeValgt.set(false);
        this.skjema().controls.logo.setValue(null);
    }

    //private hjelpemetoder

    private skjemaSatt(skjema: SkjemaType) {
        //sørg for å populere gruppefarge med farge fra skjema
        this.gruppefarge.set(skjema.value.farge!);
        skjema.controls.farge.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(farge => this.gruppefarge.set(farge))
    }

}
